<template>
  <div class="relative border-b-2 border-black bg-[#202020] p-0.5 shadow-lg" :style="{ height: `${height}px`, width: `${barWidth}px` }">
    <button
      v-for="key in keys"
      :key="key.midi"
      type="button"
      :data-midi="key.midi"
      :data-note="key.noteName"
      :data-assist="keyAssistIndicator(key)"
      class="absolute top-0.5 overflow-hidden transition-[filter,box-shadow,background] focus:outline-none"
      :class="keyClass(key)"
      :style="keyStyles(key)"
      :disabled="playing"
      :title="key.noteName"
      @pointerdown="$emit('audition-pitch', key.midi)"
      @click="$emit('select-pitch', key.midi)"
    >
      <span
        class="absolute inset-x-0 text-center font-semibold leading-none"
        :class="[
          key.isWhite ? 'text-[10px] text-slate-700' : 'text-[8px] text-white',
          keyAssistIndicator(key) ? 'bottom-[8px]' : 'bottom-1',
        ]"
        :style="keyLabelStyle(key)"
      >{{ key.noteName }}</span>
      <span
        v-if="keyAssistIndicator(key)"
        class="pointer-events-none absolute bottom-px rounded-full"
        :class="assistStripClass(key, keyAssistIndicator(key))"
        :style="assistStripStyles(key, keyAssistIndicator(key))"
        aria-hidden="true"
      ></span>
    </button>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { buildPianoKeyGeometry } from './synthPianoKeyboardGeometry.js'
import { assistKeyState, assistStripStyle, resolvePianoKeyState } from './synthCompositionAssist.js'
import { getSynthTrackColor } from './synthTrackColors.js'

const props = defineProps({
  minMidi: { type: Number, default: 24 },
  maxMidi: { type: Number, default: 84 },
  selectedPitch: { type: Number, default: null },
  activeKeyColors: { type: Object, default: () => new Map() },
  playbackTimeMs: { type: Number, default: 0 },
  playing: { type: Boolean, default: false },
  keyWidth: { type: Number, default: 28 },
  height: { type: Number, default: 58 },
  assist: { type: Object, default: null },
  trackColor: { type: String, default: null },
})

defineEmits(['select-pitch', 'audition-pitch'])

// Falls back to the first track colour so a key never renders colourless.
const keyColor = computed(() => props.trackColor || getSynthTrackColor(0).solid)
const geometry = computed(() => buildPianoKeyGeometry(props.minMidi, props.maxMidi, props.keyWidth))
const keys = computed(() => geometry.value.keys)
const barWidth = computed(() => geometry.value.width + 4)

const WHITE_KEY_CLASS = 'z-10 border border-black bg-gradient-to-b from-white via-slate-100 to-slate-300 shadow-[inset_0_-5px_8px_rgba(0,0,0,0.12)] hover:brightness-105'
const BLACK_KEY_CLASS = 'z-20 border border-black bg-gradient-to-b from-slate-700 via-slate-900 to-black shadow-[0_3px_3px_rgba(0,0,0,0.65),inset_0_-5px_5px_rgba(255,255,255,0.12)] hover:brightness-125'
// The hint is drawn inside the key as a strip, so gradients, borders and the dark
// background cannot swallow it. Roots get a thicker strip; the colour comes from the
// active track (assistStripStyle) so both key types keep one hue.
const ASSIST_STRIP_CLASS = {
  root: { white: 'inset-x-1 h-[5px]', black: 'inset-x-[3px] h-[5px]' },
  'in-scale': { white: 'inset-x-1 h-[3px]', black: 'inset-x-[3px] h-[3px]' },
}

function keyState(key) {
  return resolvePianoKeyState({
    isActive: (getActiveKeyState(key.midi)?.count ?? 0) > 0,
    isSelected: props.selectedPitch === key.midi,
    assistState: assistKeyState(key.midi, props.assist),
  })
}

// Empty string keeps the attribute absent for keys without a hint.
function keyAssistIndicator(key) {
  const state = keyState(key)
  return state === 'root' || state === 'in-scale' ? state : ''
}

function assistStripClass(key, indicator) {
  return ASSIST_STRIP_CLASS[indicator]?.[key.isWhite ? 'white' : 'black'] ?? ''
}

function assistStripStyles(key, indicator) {
  return assistStripStyle({ color: keyColor.value, indicator, isWhiteKey: key.isWhite })
}

function keyClass(key) {
  const state = keyState(key)
  if (state === 'active') {
    return props.selectedPitch === key.midi
      ? 'z-30 border border-black ring-2 ring-cyan-100 ring-inset brightness-110 bg-transparent'
      : 'z-30 border border-black ring-2 ring-white ring-inset brightness-110 bg-transparent'
  }
  if (state === 'selected') return 'z-30 border border-black ring-2 ring-white/70 ring-inset brightness-110'
  return key.isWhite ? WHITE_KEY_CLASS : BLACK_KEY_CLASS
}

function keyStyles(key) {
  const baseStyle = keyStyle(key)
  const fill = keyFillColor(key)
  if (!fill) return baseStyle
  return {
    ...baseStyle,
    backgroundColor: fill,
    backgroundImage: 'none',
    color: getReadableKeyTextColor(key.isWhite, fill),
  }
}

// Sounding keys keep the playback colour; a selected key follows the active track.
function keyFillColor(key) {
  const activeState = getActiveKeyState(key.midi)
  if (activeState?.count) return activeState.color
  return keyState(key) === 'selected' ? keyColor.value : null
}

function keyLabelStyle(key) {
  const fill = keyFillColor(key)
  return fill ? { color: getReadableKeyTextColor(key.isWhite, fill) } : null
}

function getActiveKeyState(midi) {
  const numericMidi = Number(midi)
  return props.activeKeyColors?.get?.(numericMidi) ?? props.activeKeyColors?.get?.(String(numericMidi))
}

function keyStyle(key) {
  if (key.isWhite) return {
    left: `${key.x + 2}px`,
    width: `${key.width}px`,
    height: `${props.height - 4}px`,
    borderRadius: '0 0 3px 3px',
  }
  return {
    left: `${key.x + 2}px`,
    width: `${key.width}px`,
    height: '62%',
    borderRadius: '0 0 3px 3px',
  }
}

function getReadableKeyTextColor(isWhiteKey, backgroundColor) {
  if (!backgroundColor) return isWhiteKey ? '#334155' : '#ffffff'
  const { r, g, b } = parseRgb(backgroundColor)
  const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255
  return luminance > 0.62 ? '#0f172a' : '#ffffff'
}

function parseRgb(color) {
  const hex = color?.trim?.()
  if (!hex) return { r: 255, g: 255, b: 255 }
  if (hex.startsWith('#')) {
    const value = hex.slice(1)
    if (value.length === 3) {
      return {
        r: parseInt(value[0] + value[0], 16),
        g: parseInt(value[1] + value[1], 16),
        b: parseInt(value[2] + value[2], 16),
      }
    }
    if (value.length >= 6) {
      return {
        r: parseInt(value.slice(0, 2), 16),
        g: parseInt(value.slice(2, 4), 16),
        b: parseInt(value.slice(4, 6), 16),
      }
    }
  }
  return { r: 255, g: 255, b: 255 }
}
</script>
